import type { ProductInfo, SalesChecklist } from "@expert/contentful";

import {
    EligibleProduct,
    type OfferChecklistKey,
    type OfferChecklistState,
    useOfferChecklistStore,
    useProductOfferStore,
} from "@expert/sales-tools";

import { EligibilityStatus, SaleProgress, type SalesProvider } from "@/sales-tools/home-product/types";
import { useSessionStore } from "@/sdk/sessions/session.store";

import { getRootDispatcher } from "./rootDispatcher";

type HoneycombEvent = {
    selectedProduct?: string;
    eligibility?: HoneycombEligibleProducts[];
    enrollment?: HoneycombEnrolledProducts[];
    salesOfferChecklist?: HoneycombChecklist;
};

export type HoneycombEligibleProducts = {
    productSku: string;
    productOfferId: string;
    eligibilityStatus?: EligibilityStatus;
    displayName?: string;
};

export type HoneycombEnrolledProducts = {
    productOfferId: string;
    enrollmentStatus: SaleProgress;
    statusOrigin: SalesProvider;
    productSku: string;
    displayName?: string;
};

export type HoneycombChecklist = {
    title: string;
    checklistItems?: HoneycombChecklistItem[];
}[];

type HoneycombChecklistItem = {
    phrase: string;
    subheading?: string;
    key: string;
    isCompleted: boolean;
};

export function sendToHoneycomb(sessionId: string, overrides?: Partial<HoneycombEvent>) {
    const products = useProductOfferStore.getState().getProducts(sessionId);
    const selectedProduct = useProductOfferStore.getState().getSelectedProduct(sessionId);
    const primaryProducts = useProductOfferStore.getState().getPrimaryProducts(sessionId);
    const honeycombEvent = buildHoneycombEvent(sessionId, products, primaryProducts, selectedProduct, overrides);
    const dispatcher = getRootDispatcher()
        .withMeta({ Channel: useSessionStore.getState().getSession(sessionId)?.currentTask?.type })
        .withExtra(honeycombEvent);
    dispatcher.dispatchBusinessEvent("HoneycombSalesEvent");
}

export function buildHoneycombEvent(
    sessionId: string,
    products: EligibleProduct[],
    primaryProducts: EligibleProduct[],
    selectedProduct?: EligibleProduct,
    overrides?: Partial<HoneycombEvent>,
): HoneycombEvent {
    const eligibleProducts = products
        .filter((p) => p.status === EligibilityStatus.Eligible)
        .map((p) => {
            return {
                productSku: p.productSku,
                productOfferId: p.id,
                eligibilityStatus: p.status,
                displayName: extractProductInfo(products, p.productSku)?.displayName,
            };
        });
    const enrolledProducts = products
        .filter((p) => p.saleProgress === SaleProgress.Success)
        .map((p) => {
            return {
                productOfferId: p.id,
                enrollmentStatus: p.saleProgress,
                statusOrigin: p.salesProvider,
                productSku: p.productSku,
                displayName: extractProductInfo(products, p.productSku)?.displayName,
            };
        });

    const currentProduct = selectedProduct ?? primaryProducts.at(0);
    const currentProductInfo = extractProductInfo(products, currentProduct?.productSku);
    const salesOfferChecklist = currentProductInfo ? currentProductInfo.salesOfferChecklist : undefined;
    const detectedKeys = useOfferChecklistStore.getState().getData(sessionId);
    const offerChecklistState = toOfferChecklistState(detectedKeys, salesOfferChecklist);
    const baseEvent: HoneycombEvent = {
        selectedProduct: selectedProduct?.productSku,
        eligibility: eligibleProducts,
        enrollment: enrolledProducts,
        salesOfferChecklist: currentProduct?.status === EligibilityStatus.Eligible ? offerChecklistState : undefined,
    };
    return {
        ...baseEvent,
        ...overrides,
    };
}

export function toOfferChecklistState(
    detectedKeys: OfferChecklistState["data"],
    checklist?: SalesChecklist,
): HoneycombChecklist | undefined {
    if (!checklist) {
        return undefined;
    }
    const checklistState = checklist.checklistSections.map((section) => {
        return {
            title: section.title,
            checklistItems: section.checklistItems.map((item) => {
                return {
                    phrase: item.phrase,
                    subheading: item.subheading,
                    key: item.key,
                    isCompleted: !!detectedKeys[item.key as OfferChecklistKey],
                };
            }),
        };
    });
    return checklistState;
}

function extractProductInfo(products: EligibleProduct[], sku?: string): ProductInfo | undefined {
    return products.find((product) => product.productSku === sku)?.productInfo?.["en-US"];
}
